import * as z from "zod/mini"

import {
	blogPostsBlockLayouts,
	blogPostsBlockMaxPostCount,
	footerSocialPlatforms,
	htmlEmbedBlockContentSchema,
	landingEmbeddedVideoSchema,
	landingImageSchema,
	landingPageCopyCharacterLimits,
	landingPageCopySchema,
	landingPageLinkSchema,
	landingPageTextSizes,
	lenientLandingPageCopySchema,
	productBlockLinkTargets,
	productHighlightProductTypes,
	productListTypes,
} from "../schemas/landing"
import { upgradeLegacyLandingPageButton } from "../utilities/landingLink"
import { getPlainOrLexicalTextCharacterCount } from "../utilities/lexical"

/** Builds a copy field. Saving enforces the character limits; reading stored rows must not, because a
 * limit can be tightened after the copy was written. */
type CopySchema = (limit: number) => z.ZodMiniString<string>

type AiEligibility = {
	/** Whether a website edit chat request may add the block ("create") or only rewrite an existing one. */
	websiteEdit: "create" | "rewrite" | null
	/** Whether the landing page generator may emit the block. */
	landingPageGeneration: boolean
}

// Curried so the content type is known before the rest of the definition is checked against it.
const defineBlock =
	<Schema extends z.ZodMiniType>(createContentSchema: (copy: CopySchema) => Schema) =>
	<const Ai extends AiEligibility>(definition: {
		label: string
		createDefaultContent: () => z.output<Schema>
		/** Maps every field that holds plain text or Lexical JSON, which viewers render as rich text. */
		mapRichText?: (content: z.output<Schema>, map: (value: string) => string) => z.output<Schema>
		/** Block types that used to be stored for this block, each upgrading its stored content. */
		legacyTypes?: Record<string, (content: unknown) => unknown>
		ai: Ai
	}) => ({ createContentSchema, ...definition })

const mapOptional = (value: string | undefined, map: (value: string) => string) => (value ? map(value) : value)

const createButtonSchema = (copy: CopySchema) =>
	z.nullable(
		z.pipe(
			z.transform(upgradeLegacyLandingPageButton),
			z.object({ label: copy(landingPageCopyCharacterLimits.shortLabel), link: landingPageLinkSchema }),
		),
	)

const upgradeLegacyProductList = (productType: (typeof productListTypes)[number]) => (content: unknown) => ({
	...(content as object),
	productType,
})

// The editor offers blocks in this order, and the AI output schemas list their block types in it, so
// reordering changes those schemas and requires bumping the affected AI generation versions.
const definitions = {
	header: defineBlock(() => z.object({ isAuthVisible: z._default(z.boolean(), true) }))({
		label: "Header",
		createDefaultContent: () => ({ isAuthVisible: true }),
		// Site structure the editor manages.
		ai: { websiteEdit: null, landingPageGeneration: false },
	}),
	hero: defineBlock(copy =>
		z.object({
			title: copy(landingPageCopyCharacterLimits.title),
			description: copy(landingPageCopyCharacterLimits.description),
			button: createButtonSchema(copy),
			layout: z.enum(["split", "centered"]),
			image: z.optional(landingImageSchema),
			media: z.optional(landingEmbeddedVideoSchema),
			backgroundMedia: z.optional(
				z.object({
					type: z.enum(["image", "video"]),
					url: z.string().check(z.trim()),
				}),
			),
		}),
	)({
		label: "Hero",
		createDefaultContent: () => ({ title: "", description: "", button: null, layout: "centered" }),
		mapRichText: (content, map) => ({ ...content, description: map(content.description) }),
		// The landing page generator writes the hero through its own schema.
		ai: { websiteEdit: "create", landingPageGeneration: false },
	}),
	text: defineBlock(copy =>
		z.object({
			text: copy(landingPageCopyCharacterLimits.body),
			size: z._default(z.enum(landingPageTextSizes), "medium"),
		}),
	)({
		label: "Text",
		createDefaultContent: () => ({ text: "", size: "medium" }),
		mapRichText: (content, map) => ({ ...content, text: map(content.text) }),
		ai: { websiteEdit: "create", landingPageGeneration: true },
	}),
	"html-embed": defineBlock(() => htmlEmbedBlockContentSchema)({
		label: "HTML Embed",
		createDefaultContent: () => ({ html: "", height: 400 }),
		// Creator-managed code, intentionally excluded from AI writing.
		ai: { websiteEdit: null, landingPageGeneration: false },
	}),
	gallery: defineBlock(copy =>
		z.object({
			title: z.optional(copy(landingPageCopyCharacterLimits.title)),
			images: z
				.array(
					z.object({
						url: z.string().check(z.trim()),
						alt: z.optional(z.string().check(z.trim())),
						label: z.optional(copy(landingPageCopyCharacterLimits.title)),
					}),
				)
				.check(z.minLength(1)),
		}),
	)({
		label: "Gallery",
		createDefaultContent: () => ({ images: [{ url: "", alt: "" }] }),
		// A gallery needs images the creator uploaded, so a chat request can only rewrite one in place.
		ai: { websiteEdit: "rewrite", landingPageGeneration: true },
	}),
	"two-columns": defineBlock(copy =>
		z.object({
			image: z.optional(landingImageSchema),
			media: z.optional(landingEmbeddedVideoSchema),
			title: copy(landingPageCopyCharacterLimits.title),
			description: copy(landingPageCopyCharacterLimits.body),
			button: createButtonSchema(copy),
			inverted: z._default(z.boolean(), false),
		}),
	)({
		label: "Two Columns",
		createDefaultContent: () => ({
			image: { url: "", alt: "" },
			title: "",
			description: "",
			button: null,
			inverted: false,
		}),
		mapRichText: (content, map) => ({ ...content, description: map(content.description) }),
		ai: { websiteEdit: "create", landingPageGeneration: true },
	}),
	features: defineBlock(copy =>
		z.object({
			title: z.optional(copy(landingPageCopyCharacterLimits.title)),
			features: z
				.array(
					z.object({
						icon: z.optional(z.string().check(z.trim())),
						title: copy(landingPageCopyCharacterLimits.title),
						description: z.optional(copy(landingPageCopyCharacterLimits.itemDescription)),
					}),
				)
				.check(z.minLength(1)),
		}),
	)({
		label: "Features",
		createDefaultContent: () => ({ features: [{ title: "", description: "" }] }),
		mapRichText: (content, map) => ({
			...content,
			features: content.features.map(feature => ({
				...feature,
				description: mapOptional(feature.description, map),
			})),
		}),
		ai: { websiteEdit: "create", landingPageGeneration: true },
	}),
	testimonials: defineBlock(copy =>
		z.object({
			title: z.optional(copy(landingPageCopyCharacterLimits.title)),
			testimonials: z
				.array(
					z
						.object({
							avatar: z.optional(landingImageSchema),
							name: copy(landingPageCopyCharacterLimits.shortLabel),
							role: z.optional(copy(landingPageCopyCharacterLimits.shortLabel)),
							company: z.optional(copy(landingPageCopyCharacterLimits.shortLabel)),
							quote: z.optional(copy(landingPageCopyCharacterLimits.itemDescription)),
							rating: z.optional(z.int().check(z.minimum(1), z.maximum(5))),
							media: z.optional(landingEmbeddedVideoSchema),
						})
						.check(
							z.refine<{ quote?: string; media?: unknown }>(
								testimonial =>
									testimonial.media !== undefined ||
									getPlainOrLexicalTextCharacterCount(testimonial.quote ?? "") > 0,
								{ message: "Quote is required unless a video is provided." },
							),
						),
				)
				.check(z.minLength(1)),
		}),
	)({
		label: "Testimonials",
		// Deliberately unsaveable until the creator writes a quote or adds a video.
		createDefaultContent: () => ({ title: "Testimonials", testimonials: [{ name: "", quote: "" }] }),
		mapRichText: (content, map) => ({
			...content,
			testimonials: content.testimonials.map(testimonial => ({
				...testimonial,
				quote: mapOptional(testimonial.quote, map),
			})),
		}),
		ai: { websiteEdit: "create", landingPageGeneration: false },
	}),
	faq: defineBlock(copy =>
		z.object({
			title: z.optional(copy(landingPageCopyCharacterLimits.title)),
			items: z
				.array(
					z.object({
						question: copy(landingPageCopyCharacterLimits.title),
						answer: copy(landingPageCopyCharacterLimits.itemDescription),
					}),
				)
				.check(z.minLength(1)),
		}),
	)({
		label: "FAQ",
		createDefaultContent: () => ({
			title: "Frequently asked questions",
			items: [{ question: "Question", answer: "" }],
		}),
		mapRichText: (content, map) => ({
			...content,
			items: content.items.map(item => ({ ...item, answer: map(item.answer) })),
		}),
		ai: { websiteEdit: "create", landingPageGeneration: true },
	}),
	"product-list": defineBlock(copy =>
		z.object({
			productType: z.enum(productListTypes),
			title: z.optional(copy(landingPageCopyCharacterLimits.title)),
			productIds: z.optional(z.array(z.int().check(z.positive()))),
			linkTarget: z.optional(z.enum(productBlockLinkTargets)),
		}),
	)({
		label: "Product List",
		createDefaultContent: () => ({ productType: "course" }),
		// BACKWARDS COMPAT: Old per-type product list blocks are converted to "product-list" when read.
		// Remove these once every stored row has been resaved with the new block type, together with the
		// deprecated LandingPage*Block aliases in ./landing.ts.
		legacyTypes: {
			courses: upgradeLegacyProductList("course"),
			videos: upgradeLegacyProductList("video"),
			downloadables: upgradeLegacyProductList("downloadable"),
			sessions: upgradeLegacyProductList("session"),
			events: upgradeLegacyProductList("event"),
		},
		ai: { websiteEdit: "create", landingPageGeneration: true },
	}),
	"product-highlight": defineBlock(copy =>
		z.object({
			title: z.optional(copy(landingPageCopyCharacterLimits.title)),
			productType: z.enum(productHighlightProductTypes),
			productId: z.nullable(z.int().check(z.positive())),
			linkTarget: z.optional(z.enum(productBlockLinkTargets)),
		}),
	)({
		label: "Product Highlight",
		createDefaultContent: () => ({ productType: "course", productId: null }),
		ai: { websiteEdit: "create", landingPageGeneration: true },
	}),
	"blog-posts": defineBlock(copy =>
		z.object({
			title: z.optional(copy(landingPageCopyCharacterLimits.title)),
			/** Shows only posts with this tag, matched case-insensitively. */
			tag: z.optional(z.string().check(z.trim(), z.minLength(1), z.maxLength(60))),
			postCount: z._default(z.int().check(z.minimum(1), z.maximum(blogPostsBlockMaxPostCount)), 3),
			layout: z._default(z.enum(blogPostsBlockLayouts), "grid"),
		}),
	)({
		label: "Blog Posts",
		createDefaultContent: () => ({ title: "Latest articles", postCount: 3, layout: "grid" }),
		// Shows posts the creator writes in the blog editor, so there is no copy for the AI to write.
		ai: { websiteEdit: null, landingPageGeneration: false },
	}),
	"lead-form": defineBlock(copy =>
		z.object({
			formKey: z.uuid(),
			kind: z.enum(["newsletter", "inquiry"]),
			title: copy(landingPageCopyCharacterLimits.title),
			description: z.optional(copy(landingPageCopyCharacterLimits.body)),
			submitLabel: copy(landingPageCopyCharacterLimits.shortLabel),
		}),
	)({
		label: "Lead Form",
		createDefaultContent: () => ({
			formKey: crypto.randomUUID(),
			kind: "newsletter",
			title: "Subscribe to our newsletter",
			description: "Get updates when we publish new content.",
			submitLabel: "Subscribe",
		}),
		mapRichText: (content, map) => ({ ...content, description: mapOptional(content.description, map) }),
		ai: { websiteEdit: "create", landingPageGeneration: true },
	}),
	footer: defineBlock(copy =>
		z.object({
			companyName: z.optional(copy(landingPageCopyCharacterLimits.title)),
			address: z.optional(copy(landingPageCopyCharacterLimits.description)),
			email: z.optional(z.string().check(z.trim())),
			socialLinks: z.optional(
				z.array(
					z.object({
						platform: z.enum(footerSocialPlatforms),
						url: z.string().check(z.trim()),
					}),
				),
			),
		}),
	)({
		label: "Footer",
		createDefaultContent: () => ({ companyName: "", address: "", email: "", socialLinks: [] }),
		// Site structure the editor manages, so a chat request can only rewrite its copy.
		ai: { websiteEdit: "rewrite", landingPageGeneration: false },
	}),
}

type Definitions = typeof definitions

export type LandingPageBlockType = keyof Definitions
export type LandingPageBlockContent<Type extends LandingPageBlockType> = z.output<
	ReturnType<Definitions[Type]["createContentSchema"]>
>
// Distributes over a union of types, so a block of one of several types is a union of blocks.
export type LandingPageBlockOf<Type extends LandingPageBlockType> = Type extends LandingPageBlockType
	? { type: Type; content: LandingPageBlockContent<Type> }
	: never
export type LandingPageBlock = LandingPageBlockOf<LandingPageBlockType>

type ContentSchema<Type extends LandingPageBlockType> = z.ZodMiniType<LandingPageBlockContent<Type>>

export type LandingBlockDefinition<Type extends LandingPageBlockType> = {
	type: Type
	label: string
	/** Validates content a creator saves, enforcing the copy character limits. */
	contentSchema: ContentSchema<Type>
	/** Reads stored content, which may predate the current copy character limits. */
	lenientContentSchema: ContentSchema<Type>
	createDefaultBlock: () => LandingPageBlockOf<Type>
	ai: Definitions[Type]["ai"]
}

export const landingPageBlockTypes = Object.keys(definitions) as LandingPageBlockType[]

// Every definition with its content type erased, so the lookups below can treat them alike. Methods
// rather than function properties, because only method parameters accept a narrower content type.
type AnyDefinition = {
	label: string
	createContentSchema(copy: CopySchema): z.ZodMiniType
	createDefaultContent(): unknown
	mapRichText?(content: unknown, map: (value: string) => string): unknown
	legacyTypes?: Record<string, (content: unknown) => unknown>
	ai: AiEligibility
}
const anyDefinitions: Record<LandingPageBlockType, AnyDefinition> = definitions

const blockDefinitions = Object.fromEntries(
	landingPageBlockTypes.map(type => {
		const definition = anyDefinitions[type]
		return [
			type,
			{
				type,
				label: definition.label,
				contentSchema: definition.createContentSchema(landingPageCopySchema),
				lenientContentSchema: definition.createContentSchema(lenientLandingPageCopySchema),
				createDefaultBlock: () => ({ type, content: definition.createDefaultContent() }),
				ai: definition.ai,
			},
		]
	}),
) as { [Type in LandingPageBlockType]: LandingBlockDefinition<Type> }

export function blockDefinition<Type extends LandingPageBlockType>(type: Type): LandingBlockDefinition<Type> {
	return blockDefinitions[type]
}

export function isLandingPageBlockType(type: string): type is LandingPageBlockType {
	return Object.hasOwn(definitions, type)
}

const legacyBlockTypes = new Map(
	landingPageBlockTypes.flatMap(type =>
		Object.entries(anyDefinitions[type].legacyTypes ?? {}).map(
			([legacyType, upgrade]) => [legacyType, { type, upgrade }] as const,
		),
	),
)

/** Reads a stored block row, upgrading legacy block types. Copy written under older limits is kept. */
export function parseStoredBlock(row: { type: string; content: string }): LandingPageBlock {
	let type: string = row.type
	let content: unknown = JSON.parse(row.content)

	const legacy = legacyBlockTypes.get(type)
	if (legacy !== undefined) {
		type = legacy.type
		content = legacy.upgrade(content)
	}
	if (!isLandingPageBlockType(type)) throw new Error(`Unknown landing page block type: ${row.type}`)

	return { type, content: blockDefinitions[type].lenientContentSchema.parse(content) } as LandingPageBlock
}

/** Validates a block a creator saves, returning it with defaults applied and copy trimmed. */
export function validateBlock<Block extends LandingPageBlock>(block: Block): Block {
	const { contentSchema } = blockDefinitions[block.type] as LandingBlockDefinition<LandingPageBlockType>
	return { type: block.type, content: contentSchema.parse(block.content) } as Block
}

/** Applies `map` to every copy field a viewer renders as rich text. */
export function mapBlockRichText<Block extends LandingPageBlock>(block: Block, map: (value: string) => string): Block {
	const definition = anyDefinitions[block.type]
	if (definition.mapRichText === undefined) return block
	return { ...block, content: definition.mapRichText(block.content, map) as Block["content"] }
}

function createBlockSchema(lenient: boolean): z.ZodMiniType<LandingPageBlock, LandingPageBlock> {
	return z.discriminatedUnion(
		"type",
		landingPageBlockTypes.map(type =>
			z.object({
				type: z.literal(type),
				content: lenient ? blockDefinitions[type].lenientContentSchema : blockDefinitions[type].contentSchema,
			}),
		) as unknown as [z.ZodMiniObject, ...z.ZodMiniObject[]],
	) as unknown as z.ZodMiniType<LandingPageBlock, LandingPageBlock>
}

export const landingPageBlockSchema = createBlockSchema(false)
export const lenientLandingPageBlockSchema = createBlockSchema(true)

type BlockTypesWhere<Condition> = {
	[Type in LandingPageBlockType]: Definitions[Type]["ai"] extends Condition ? Type : never
}[LandingPageBlockType]

export type WebsiteEditCreatableBlockType = BlockTypesWhere<{ websiteEdit: "create" }>
export type WebsiteEditRewritableBlockType = BlockTypesWhere<{ websiteEdit: "create" | "rewrite" }>
export type LandingPageGeneratedBlockType = BlockTypesWhere<{ landingPageGeneration: true }>

export const websiteEditCreatableBlockTypes = landingPageBlockTypes.filter(
	type => anyDefinitions[type].ai.websiteEdit === "create",
) as WebsiteEditCreatableBlockType[]

export const websiteEditRewritableBlockTypes = landingPageBlockTypes.filter(
	type => anyDefinitions[type].ai.websiteEdit !== null,
) as WebsiteEditRewritableBlockType[]

export const landingPageGeneratedBlockTypes = landingPageBlockTypes.filter(
	type => anyDefinitions[type].ai.landingPageGeneration,
) as LandingPageGeneratedBlockType[]
